import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";

const SCENARIOS = [
  {
    title: "Семейный ужин",
    desc: "Один-два пирога к ужину вместо готовки. Берём с мясом или капустой — сытно, быстро, по-домашнему.",
    detail: "Рекомендуем: мясной пирог, пирог с капустой, пирожки жареные",
    bg: "bg-[#F2EBE1]",
    color: "text-[#2C2520]",
  },
  {
    title: "Офис и корпоратив",
    desc: "Пироги к совещанию, день рождения коллеги или просто к пятнице. Режутся на порции, не требуют посуды.",
    detail: "Рекомендуем: ассорти из 3–4 видов, пирожки сытные и сладкие",
    bg: "bg-[#2C2520]",
    color: "text-[#F9F6F0]",
  },
  {
    title: "Праздник и гости",
    desc: "День рождения, юбилей, встреча Нового года — пироги накрывают стол на любую компанию без лишних хлопот.",
    detail: "Рекомендуем: несколько больших пирогов на разные вкусы",
    bg: "bg-[#C65D3B]",
    color: "text-white",
  },
  {
    title: "Свадьба",
    desc: "Традиционный каравай с украшениями. Принимаем заказы заранее — уточняем детали оформления по телефону.",
    detail: "Каравай на заказ — звоните за 3–5 дней",
    bg: "bg-[#F2EBE1]",
    color: "text-[#2C2520]",
  },
  {
    title: "Поминальный обед",
    desc: "Пироги для поминальных обедов — постные и скоромные. Принимаем заказы накануне, доставляем к нужному времени.",
    detail: "Постное меню — без мяса, яиц и молочных продуктов",
    bg: "bg-white border border-[#E8E3DA]",
    color: "text-[#2C2520]",
  },
  {
    title: "Корпоративный заказ",
    desc: "Регулярная поставка в офис или на производство. Обсуждаем объём и периодичность индивидуально.",
    detail: "Позвоните — обсудим условия постоянного заказа",
    bg: "bg-[#2C2520]",
    color: "text-[#F9F6F0]",
  },
];

const PirogiNaZakaz = () => {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="pirogi-na-zakaz-page">
      <Helmet>
        <title>Пироги на заказ в Ульяновске — домашняя выпечка для дома, офиса и праздника</title>
        <meta name="description" content="Пироги на заказ в Ульяновске. Семейный ужин, офис, день рождения, свадьба, поминальный обед. 30+ видов выпечки, ручная работа, доставка по городу. +7 937 876-45-37." />
        <link rel="canonical" href="https://pirogi73.ru/pirogi-na-zakaz/" />
        <meta property="og:title" content="Пироги на заказ в Ульяновске | Ишеевские пироги" />
        <meta property="og:description" content="Домашняя выпечка на любой случай — от семейного ужина до свадебного каравая. Доставка по Ульяновску." />
        <meta property="og:url" content="https://pirogi73.ru/pirogi-na-zakaz/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://pirogi73.ru/#business",
          "name": "Ишеевские пироги",
          "telephone": "+79378764537",
          "url": "https://pirogi73.ru",
          "address": { "@type": "PostalAddress", "addressLocality": "Ульяновск", "addressRegion": "Ульяновская область", "addressCountry": "RU" },
          "areaServed": { "@type": "City", "name": "Ульяновск" },
          "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "09:00", "closes": "21:00" }
        })}</script>
      </Helmet>

      <div className="label-eyebrow mb-4">Пироги на заказ</div>
      <h1 className="font-display text-5xl sm:text-6xl text-[#2C2520] leading-tight mb-6">
        Пироги на заказ
        <br />
        в Ульяновске
      </h1>
      <p className="text-lg text-[#5C4F45] max-w-2xl leading-relaxed mb-16">
        Готовим под любой повод — от пирога к ужину до свадебного каравая. Закажите по телефону или через каталог, укажите адрес и время — привезём свежим.
      </p>

      <h2 className="font-display text-3xl text-[#2C2520] mb-8">Для каких случаев заказывают</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {SCENARIOS.map((s, i) => (
          <div key={i} className={`${s.bg} ${s.color} rounded-[24px] p-8`}>
            <h3 className="font-display text-2xl mb-3">{s.title}</h3>
            <p className={`text-sm leading-relaxed mb-4 ${s.color === "text-[#2C2520]" ? "text-[#5C4F45]" : "opacity-85"}`}>{s.desc}</p>
            <div className={`text-xs font-semibold tracking-wide ${s.color === "text-[#2C2520]" ? "text-[#C65D3B]" : "opacity-70"}`}>{s.detail}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E8E3DA] rounded-[28px] p-10 mb-12">
        <h2 className="font-display text-3xl text-[#2C2520] mb-8">Как сделать заказ</h2>
        <div className="grid sm:grid-cols-2 gap-8">
          <div>
            <h3 className="font-display text-xl text-[#2C2520] mb-4">Через сайт</h3>
            <ol className="space-y-3 text-[#5C4F45]">
              {[
                "Выберите пироги в каталоге",
                "Добавьте в корзину нужное количество",
                "Укажите адрес и время доставки",
                "Подтвердите — перезвоним для уточнений",
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#C65D3B] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h3 className="font-display text-xl text-[#2C2520] mb-4">По телефону</h3>
            <p className="text-[#5C4F45] mb-4">Звоните — назовите что хотите, адрес и время. Менеджер подтвердит заказ и уточнит детали.</p>
            <a href="tel:+79378764537" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C2520] text-[#F9F6F0] hover:bg-[#1c1714] transition-colors font-medium">
              <Phone size={16} />
              +7 937 876-45-37
            </a>
          </div>
        </div>
      </div>

      <div className="bg-[#F2EBE1] border border-[#E8E3DA] rounded-[28px] p-8 mb-12">
        <h2 className="font-display text-2xl text-[#2C2520] mb-6">Что важно знать</h2>
        <div className="grid sm:grid-cols-2 gap-4 text-[#5C4F45]">
          {[
            "Минимальный заказ от 1500 ₽",
            "Бесплатная доставка по городу от 3000 ₽",
            "Оплата наличными или картой при получении",
            "Для срочных заказов звоните за 2–3 часа",
            "Большие заказы (праздник, свадьба) — за 1–2 дня",
            "Работаем без выходных с 9:00 до 21:00",
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <span className="text-[#C65D3B] font-bold mt-0.5">✓</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center flex flex-wrap gap-4 justify-center">
        <Link to="/" className="btn-primary" data-testid="pirogi-na-zakaz-cta">
          Выбрать пироги →
        </Link>
        <Link to="/dostavka-pirogov-ulyanovsk/" className="inline-flex items-center px-6 py-3 rounded-full border border-[#E8E3DA] text-[#2C2520] hover:border-[#C65D3B] transition-colors">
          Условия доставки
        </Link>
      </div>
    </div>
  );
};

export default PirogiNaZakaz;
